import { useCallback, useSyncExternalStore } from "react";

const REMOVED_GROUP_TTL_MS = 2 * 60 * 1000;
const EMPTY: ReadonlySet<number> = new Set<number>();

let removedByObject = new Map<string, ReadonlySet<number>>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const listeners = new Set<() => void>();

const objectKey = (corporateId: string | number, objectId: number) =>
  `${corporateId}:${objectId}`;

const emit = () => listeners.forEach((l) => l());

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const update = (key: string, next: ReadonlySet<number>) => {
  const copy = new Map(removedByObject);
  if (next.size) copy.set(key, next);
  else copy.delete(key);
  removedByObject = copy;
  emit();
};

const unmark = (key: string, groupId: number) => {
  const current = removedByObject.get(key);
  if (!current?.has(groupId)) return;
  const next = new Set(current);
  next.delete(groupId);
  update(key, next);
};

export const markGroupEnrollmentRemoved = (
  corporateId: string | number,
  objectId: number,
  groupId: number,
) => {
  const key = objectKey(corporateId, objectId);
  const timerKey = `${key}:${groupId}`;
  const pending = timers.get(timerKey);
  if (pending) clearTimeout(pending);
  timers.set(
    timerKey,
    setTimeout(() => {
      timers.delete(timerKey);
      unmark(key, groupId);
    }, REMOVED_GROUP_TTL_MS),
  );
  update(key, new Set(removedByObject.get(key) ?? EMPTY).add(groupId));
};

export const clearGroupEnrollmentsRemoved = (
  corporateId: string | number,
  objectId: number,
  groupIds: number[],
) => {
  const key = objectKey(corporateId, objectId);
  groupIds.forEach((groupId) => {
    const timerKey = `${key}:${groupId}`;
    const pending = timers.get(timerKey);
    if (pending) clearTimeout(pending);
    timers.delete(timerKey);
    unmark(key, groupId);
  });
};

export const useRemovedGroupIds = (
  corporateId: string | number,
  objectId: number,
): ReadonlySet<number> => {
  const key = objectKey(corporateId, objectId);
  const getSnapshot = useCallback(
    () => removedByObject.get(key) ?? EMPTY,
    [key],
  );
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};
